//general packages
import React, { useState, useEffect } from "react";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import PrivateRoute from "./PrivateRoute";
import * as api from "./api";
import { HIDDEN_PATHS } from "./config/hiddenPaths";
import { isHiddenFor } from "./utils/pathHelpers";

//layouts
import Navbar from "./components/layouts/Navbar";
import Footer from "./components/layouts/Footer";
import SideBar from "./components/layouts/SideBar";

//pages
// import Login from "./components/pages/Login";
import Signup from "./components/pages/signup/Signup";
import SignupPartner from "./components/pages/signup/SignupPartner";
import PartnerSignup from "./components/pages/signup/PartnerSignup";
import Welcome from "./components/pages/Welcome";
import HomeDashboard from "./components/pages/HomeDashboard";
import About from "./components/pages/About";
import Contact from "./components/pages/Contact";
import PageNotFound from "./components/pages/errors/PageNotFound";
import Unauthorized from "./components/pages/errors/Unauthorized";

import Users from "./components/pages/users/Users";
import UpdateUser from "./components/pages/users/UpdateUser";

// pages for support
import SupportRequests from "./components/pages/support/SupportRequests";
import CreateTicket from "./components/pages/support/CreateTicket";
import TicketSuccess from "./components/pages/support/TicketSuccess";
import ContactSupport from "./components/pages/support/ContactSupport";

// Landing page
import LandingPage from "./components/pages/landing_page/LandingPage";

const Login = React.lazy(() => import("./components/pages/Login"));
const ForgotPassword = React.lazy(() =>
  import("./components/pages/ForgotPassword")
);

// Patients
const Patients = React.lazy(() =>
  import("./components/pages/patients/Patients")
);
const PatientPage = React.lazy(() =>
  import("./components/pages/patients/PatientPage")
);
const CreatePatient = React.lazy(() =>
  import("./components/pages/patients/CreatePatient")
);
const UpdatePatient = React.lazy(() =>
  import("./components/pages/patients/UpdatePatient")
);
const AddMedication = React.lazy(() =>
  import("./components/pages/patients/datacategories/AddMedication")
);
const ReadMedications = React.lazy(() =>
  import("./components/pages/patients/datacategories/ReadMedications")
);
const UpdateMedication = React.lazy(() =>
  import("./components/pages/patients/datacategories/UpdateMedication")
);

// Schedules
const Schedules = React.lazy(() =>
  import("./components/pages/schedules/Schedules")
);

const Tasks = React.lazy(() => import("./components/pages/tasks/Tasks"));

// const ProgressNotes = React.lazy(() =>
//   import("./components/pages/progress_notes/ProgressNotes")
// );
// const Assessments = React.lazy(() =>
//   import("./components/pages/assessments/Assessments")
// );
// const CarePlans = React.lazy(() =>
//   import("./components/pages/careplans/CarePlans")
// );
const Loved1s = React.lazy(() => import("./components/pages/loved1s/Loved1s"));

const ResidentLoved1s = React.lazy(() =>
  import("./components/pages/loved1s/ResidentLoved1s")
);

const PracticeThread = React.lazy(() =>
  import("./components/pages/loved1s/PracticeThread")
);

const ThreadLists = React.lazy(() =>
  import("./components/pages/loved1s/ThreadLists")
);

const LovedOneThread = React.lazy(() =>
  import("./components/pages/loved1s/LovedOneThread")
);

const UpdateLovedOnes = React.lazy(() =>
  import("./components/pages/loved1s/UpdateLoved1s")
);

const DeletePatient = React.lazy(() =>
  import("./components/pages/patients/DeletePatient")
);
const TermsAndConditions = React.lazy(() =>
  import("./components/layouts/TermsAndConditions")
);
const Handover = React.lazy(() =>
  import("./components/pages/handovers/Handover")
);
const NoHandovers = React.lazy(() => import("./components/pages/NoHandovers"));
const AddVisit = React.lazy(() => import("./components/pages/visits/AddVisit"));
const ViewVisits = React.lazy(() =>
  import("./components/pages/visits/ViewVisits")
);
const FacilityVisitDetails = React.lazy(() =>
  import("./components/pages/visits/FacilityVisitDetails")
);
const Treatments = React.lazy(() =>
  import("./components/pages/treatments/Treatments")
);

const VisitDetails = React.lazy(() =>
  import("./components/pages/visits/VisitDetails")
);
const ActivityDetails = React.lazy(() =>
  import("./components/pages/visits/ActivityDetails")
);
const Handovers = React.lazy(() =>
  import("./components/pages/handovers/Handovers")
);

// imports for allergies page
const Allergies = React.lazy(() =>
  import("./components/pages/allergies/Allergies")
);
const Medications = React.lazy(() =>
  import("./components/pages/medications/Medications")
);
const AddConsent = React.lazy(() =>
  import("./components/pages/consents/AddConsent")
);
const ConsentLists = React.lazy(() =>
  import("./components/pages/consents/ConsentLists")
);

const RequestConsent = React.lazy(() =>
  import("./components/pages/consents/RequestConsent")
);

const ReleaseNotes = React.lazy(() =>
  import("./components/pages/release_notes/ReleaseNotes")
);

const Referrals = React.lazy(() =>
  import("./components/pages/referrals/Referral")
);

const Referrallists = React.lazy(() =>
  import("./components/pages/referrals/ReferralLists")
);

const GeneralCareHome = React.lazy(() =>
  import("./components/pages/carehome/GeneralCareHome")
);

const PodiatryProgressNotes = React.lazy(() =>
  import("./components/pages/facilities/podiatry/PodiatryProgressNotes")
);

const PodiatryAssessments = React.lazy(() =>
  import("./components/pages/facilities/podiatry/PodiatryAssessments")
);

const PodiatryCarePlans = React.lazy(() =>
  import("./components/pages/facilities/podiatry/PodiatryCarePlans")
);

const PhysioProgressNotes = React.lazy(() =>
  import("./components/pages/facilities/physiotherapy/PhysioProgressNotes")
);

const PhysioAssessments = React.lazy(() =>
  import("./components/pages/facilities/physiotherapy/PhysioAssessments")
);

const DieteticsProgressNotes = React.lazy(() =>
  import("./components/pages/facilities/dietetics/DieteticsProgressNotes")
);

const DieteticsAssessments = React.lazy(() =>
  import("./components/pages/facilities/dietetics/DieteticsAssessments")
);

const ChatPage = React.lazy(() => import("./components/pages/chats/Chat"));

const AllSettings = React.lazy(() =>
  import("./components/pages/settings/AllSettings")
);

const Notifications = React.lazy(() =>
  import("./components/pages/notifications/Notifications")
);

const RegisterAccount = React.lazy(() =>
  import("./components/pages/accountManagement/RegisterAccount")
);

const AccountLists = React.lazy(() =>
  import("./components/pages/accountManagement/AccountLists")
);

const Subscriptions = React.lazy(() =>
  import("./components/pages/subscriptions/Subscriptions")
);

const Practitioners = React.lazy(() =>
  import("./components/pages/practitioners/Practitioners")
);

const Validate = React.lazy(() => import("./Validate"));
const ProcessBookingConfirmation = React.lazy(() =>
  import("./components/booking/ProcessBooking")
);
const MultiProcessBookingConfirmation = React.lazy(() =>
  import("./components/booking/MultiProcessBooking")
);
const SuccessBookingConfirmation = React.lazy(() =>
  import("./components/booking/SuccessBooking")
);
const CancelBookingConfirmation = React.lazy(() =>
  import("./components/booking/CancelBooking")
);
const SetResidentActive = React.lazy(() =>
  import("./components/resident-status/SetActive")
);
const SetResidentInActive = React.lazy(() =>
  import("./components/resident-status/SetInActive")
);
const MultiStatusManager = React.lazy(() =>
  import("./components/resident-status/MultiStatusManager")
);
const LovedOnePayment = React.lazy(() =>
  import("./components/booking/LovedOnePayment")
);

const RegisterCareHome = React.lazy(() =>
  import("./components/pages/administrator/RegisterCareHome")
);

const RegisterEmployee = React.lazy(() =>
  import("./components/pages/administrator/RegisterEmployee")
);

const RegisterFacility = React.lazy(() =>
  import("./components/pages/administrator/RegisterFacility")
);

const EditProfile = React.lazy(() =>
  import("./components/pages/profiles/EditProfileV2")
);

const ManageAccounts = React.lazy(() =>
  import("./components/pages/accountManagement/ManageAccounts")
);

function AppWithRouterAccess() {
  const authState = JSON.parse(localStorage.getItem("auth0-token-storage"));
  const accessToken = authState?.access_token;
  let navigate = useNavigate();
  const pathname = window.location.pathname;

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState([]);
  const [isNavClosed, setIsNavClosed] = useState(false);
  const [saved, setSaved] = useState(true);
  const [confirmShowed, setConfirmShowed] = useState(false);
  const [sort, setSort] = useState("");
  const location = useLocation();
  let param =
    location?.search?.split("&")?.filter((item) => item !== "") ?? null;
  const [currentPage, setCurrentPage] = useState(
    parseInt(param[0]?.split("=")[1] ?? "0") === 0
      ? null
      : parseInt(param[0]?.split("=")[1])
  );
  const [navbarLoader, setNavBarLoader] = useState(false);
  const [notificationData, setNotificationData] = useState([]);
  const [notificationLoader, setNotificationLoader] = useState(false);
  const [unreadCount, setUnreadCount] = useState(false);

  const changeURLPatient = (
    patientId,
    page,
    tabOpen,
    facility,
    filter,
    ward,
    funding,
    inactiveResident,
    date = null
  ) => {
    if (confirmShowed) return;
    if (!saved) {
      setConfirmShowed(true);
      if (
        !window.confirm(
          "Are you sure you want to leave this page? Unsaved changes will be discarded"
        )
      ) {
        setConfirmShowed(false);
        return false;
      }
      setConfirmShowed(false);
    }
    setSaved(true);
    navigate(
      {
        search:
          `?page=${page ?? 1}&patientId=${patientId ?? "null"}&tab=${
            tabOpen ?? "1"
          }&facility=${
            facility && facility.length > 0 ? facility.join(",") : "null"
          }&filter=${filter ?? "today"}&ward=${
            ward && ward.length > 0 ? ward.join(",") : "null"
          }&funding=${funding ?? "null"}&inactiveResident=${
            inactiveResident ?? "false"
          }` + (date != null ? `&date=${date}` : ""),
      },
      { replace: true }
    );

    return true;
  };

  // for push notification
  useEffect(() => {
    const getNotifications = async () => {
      try {
        setNotificationLoader(true);
        const res = await api.getPushNotification(accessToken);
        const notif = res.data.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setNotificationData(notif);
        setNotificationLoader(false);
      } catch (error) {
        // console.log(error);
        setNotificationLoader(false);
      }
    };
    getNotifications();
  }, []);

  // useEffect(() => {
  //   generateToken();
  //   onMessage(messaging, (payload) => {
  //     console.log(payload);
  //     setNotificationData((prevArray) => [payload, ...prevArray]);
  //     toast(payload.data.event + " - " + payload.data.notes);
  //   });
  // }, []);
  if (window.location.pathname.includes("/releaseNotes/")) {
    return (
      <Routes>
        <Route path="/releaseNotes/:platform" element={<ReleaseNotes />} />
      </Routes>
    );
  }
  return (
    <div>
      {!isHiddenFor(pathname, HIDDEN_PATHS.navbar) && (
        <Navbar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          sort={sort}
          setSort={setSort}
          changeURLPatient={changeURLPatient}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          setNavBarLoader={setNavBarLoader}
          selectedOption={selectedOption}
          notificationData={notificationData}
          setNotificationData={setNotificationData}
          notificationLoader={notificationLoader}
          unreadCount={unreadCount}
          setUnreadCount={setUnreadCount}
        />
      )}

      <div className="container-fluid hapi-main-body pt-0 mb-0 pb-0">
        <div className="row">
          {isHiddenFor(pathname, HIDDEN_PATHS.sidebar) ? null : !isNavClosed ? (
            <div className="col-md-4 col-lg-3 col-xl-2 d-sm-none d-sm-block p-0 m-0 bg-white hapi-sidebar position-relative">
              {/* Note that there are Links <NavLink /> inside the sidebar below and is covered by Browser/Router and Security above */}
              <SideBar
                setIsNavClosed={setIsNavClosed}
                saved={saved}
                setSaved={setSaved}
              />
            </div>
          ) : (
            <div
              className="position-fixed"
              style={{ top: "140px", zIndex: 100 }}
            >
              <button
                className="btn btn-primary bg-light orange-1 py-4 border border-primary border-left-0"
                style={{
                  borderTopRightRadius: "25px",
                  borderBottomRightRadius: "25px",
                }}
                onClick={() => setIsNavClosed(false)}
              >
                <i className="fas fa-bars" style={{ color: "#fff" }}></i>
              </button>
            </div>
          )}

          <div
            className={`${
              isNavClosed || isHiddenFor(pathname, HIDDEN_PATHS.mainContent)
                ? "col-md-12"
                : "col-md-8 col-lg-9 col-xl-10"
            } hapi-main-content`}
          >
            {/* For switching between pages */}
            <Routes>
              {/* Private Routes */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <LandingPage
                      notificationData={notificationData}
                      notificationLoader={notificationLoader}
                    />
                  </PrivateRoute>
                }
              />
              <Route
                path="/landing-page"
                element={
                  <PrivateRoute>
                    <LandingPage
                      notificationData={notificationData}
                      notificationLoader={notificationLoader}
                    />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <HomeDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <PrivateRoute>
                    <Users />
                  </PrivateRoute>
                }
              />
              <Route
                path="/users/edit/:_id"
                element={
                  <PrivateRoute>
                    <UpdateUser />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patients"
                element={
                  <PrivateRoute>
                    <Patients
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      saved={saved}
                      setSaved={setSaved}
                      changeURLPatient={changeURLPatient}
                      confirmShowed={confirmShowed}
                      setConfirmShowed={setConfirmShowed}
                      sort={sort}
                      setSort={setSort}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      navbarLoader={navbarLoader}
                      setSelectedOption={setSelectedOption}
                      selectedOption={selectedOption}
                    />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patient"
                element={
                  <PrivateRoute>
                    <PatientPage
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      saved={saved}
                      setSaved={setSaved}
                      changeURLPatient={changeURLPatient}
                      confirmShowed={confirmShowed}
                      setConfirmShowed={setConfirmShowed}
                    />
                  </PrivateRoute>
                }
              />
              <Route
                path="/schedules"
                element={
                  <PrivateRoute>
                    <Schedules />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <PrivateRoute>
                    <Tasks />
                  </PrivateRoute>
                }
              />
              <Route
                path="/loved1s"
                element={
                  <PrivateRoute>
                    <Loved1s />
                  </PrivateRoute>
                }
              />
              <Route
                path="/loved1s/:_id"
                element={
                  <PrivateRoute>
                    <ResidentLoved1s />
                  </PrivateRoute>
                }
              />
              <Route
                path="/loved1s/add"
                element={
                  <PrivateRoute>
                    <UpdateLovedOnes />
                  </PrivateRoute>
                }
              />
              <Route
                path="/threads/:threadId"
                element={
                  <PrivateRoute>
                    <PracticeThread />
                  </PrivateRoute>
                }
              />
              <Route
                path="/threadLists/:residentId"
                element={
                  <PrivateRoute>
                    <ThreadLists />
                  </PrivateRoute>
                }
              />
              <Route
                path="/handovers/:id"
                element={
                  <PrivateRoute>
                    <Handover />
                  </PrivateRoute>
                }
              />
              <Route
                path="/handovers"
                element={
                  <PrivateRoute>
                    <Handovers />
                  </PrivateRoute>
                }
              />
              <Route
                path="/visits/addVisit"
                element={
                  <PrivateRoute>
                    <AddVisit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/visits"
                element={
                  <PrivateRoute>
                    <ViewVisits />
                  </PrivateRoute>
                }
              />
              <Route
                path="/treatments"
                element={
                  <PrivateRoute>
                    <Treatments />
                  </PrivateRoute>
                }
              />
              <Route
                path="/visits/visitDetails"
                element={
                  <PrivateRoute>
                    <VisitDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/visits/visitDetails/finalize"
                element={
                  <PrivateRoute>
                    <FacilityVisitDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/visits/activityDetails/:residentId"
                element={
                  <PrivateRoute>
                    <ActivityDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <PrivateRoute>
                    <SupportRequests />
                  </PrivateRoute>
                }
              />
              <Route
                path="/support/contact"
                element={
                  <PrivateRoute>
                    <ContactSupport />
                  </PrivateRoute>
                }
              />
              <Route
                path="/support/add"
                element={
                  <PrivateRoute>
                    <CreateTicket />
                  </PrivateRoute>
                }
              />
              <Route
                path="/support/success"
                element={
                  <PrivateRoute>
                    <TicketSuccess />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patients/add"
                element={
                  <PrivateRoute>
                    <CreatePatient />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patients/edit/:_id"
                element={
                  <PrivateRoute>
                    <UpdatePatient />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patients/delete/:_id"
                element={
                  <PrivateRoute>
                    <DeletePatient />
                  </PrivateRoute>
                }
              />
              <Route
                path="/addConsent/:_id"
                element={
                  <PrivateRoute>
                    <AddConsent />
                  </PrivateRoute>
                }
              />
              <Route
                path="/allConsents/:_id"
                element={
                  <PrivateRoute>
                    <ConsentLists />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patients/medications/add/:_id"
                element={
                  <PrivateRoute>
                    <AddMedication />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patients/medications/:_id"
                element={
                  <PrivateRoute>
                    <ReadMedications />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patients/:_id/medications/:_medId/edit"
                element={
                  <PrivateRoute>
                    <UpdateMedication />
                  </PrivateRoute>
                }
              />
              <Route
                path="/allergies/edit/"
                element={
                  <PrivateRoute>
                    <Allergies />
                  </PrivateRoute>
                }
              />
              <Route
                path="/medications/edit/"
                element={
                  <PrivateRoute>
                    <Medications />
                  </PrivateRoute>
                }
              />
              <Route
                path="/terms"
                element={
                  <PrivateRoute>
                    <TermsAndConditions />
                  </PrivateRoute>
                }
              />
              <Route
                path="/contact"
                element={
                  <PrivateRoute>
                    <Contact />
                  </PrivateRoute>
                }
              />
              <Route
                path="/handover/visitId=:visitId"
                element={
                  <PrivateRoute>
                    <NoHandovers />
                  </PrivateRoute>
                }
              />
              <Route
                path="/referrals/:_id"
                element={
                  <PrivateRoute>
                    <Referrals />
                  </PrivateRoute>
                }
              />
              <Route
                path="/referralLists/:_id"
                element={
                  <PrivateRoute>
                    <Referrallists />
                  </PrivateRoute>
                }
              />
              <Route
                path="/generalCareHome/:_id"
                element={
                  <PrivateRoute>
                    <GeneralCareHome />
                  </PrivateRoute>
                }
              />
              <Route
                path="/facilities/podiatry/progressNotes"
                element={
                  <PrivateRoute>
                    <PodiatryProgressNotes />
                  </PrivateRoute>
                }
              />
              <Route
                path="/facilities/podiatry/assessments"
                element={
                  <PrivateRoute>
                    <PodiatryAssessments />
                  </PrivateRoute>
                }
              />
              <Route
                path="/facilities/podiatry/carePlans"
                element={
                  <PrivateRoute>
                    <PodiatryCarePlans />
                  </PrivateRoute>
                }
              />
              <Route
                path="/facilities/physiotherapy/progressNotes"
                element={
                  <PrivateRoute>
                    <PhysioProgressNotes />
                  </PrivateRoute>
                }
              />
              <Route
                path="/facilities/physiotherapy/assessments"
                element={
                  <PrivateRoute>
                    <PhysioAssessments />
                  </PrivateRoute>
                }
              />
              <Route
                path="/facilities/dietetics/progressNotes"
                element={
                  <PrivateRoute>
                    <DieteticsProgressNotes />
                  </PrivateRoute>
                }
              />
              <Route
                path="/facilities/dietetics/assessments"
                element={
                  <PrivateRoute>
                    <DieteticsAssessments />
                  </PrivateRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <PrivateRoute>
                    <ChatPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute
                    allowUnapprovedUser={true}
                    allowHrManagerUser={true}
                  >
                    <AllSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <PrivateRoute>
                    <Notifications />
                  </PrivateRoute>
                }
              />
              <Route
                path="/account/register"
                element={
                  <PrivateRoute>
                    <RegisterAccount />
                  </PrivateRoute>
                }
              />
              <Route
                path="/account/lists"
                element={
                  <PrivateRoute>
                    <AccountLists />
                  </PrivateRoute>
                }
              />
              <Route
                path="/subscriptions"
                element={
                  <PrivateRoute>
                    <Subscriptions />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/register-carehome"
                element={
                  <PrivateRoute allowedRoles={["admin"]}>
                    <RegisterCareHome />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/register-employee"
                element={
                  <PrivateRoute allowedRoles={["admin"]}>
                    <RegisterEmployee />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/register-facility"
                element={
                  <PrivateRoute allowedRoles={["admin"]}>
                    <RegisterFacility />
                  </PrivateRoute>
                }
              />
              <Route
                path="/practitioners"
                element={
                  <PrivateRoute>
                    <Practitioners />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile/edit"
                element={
                  <PrivateRoute
                    allowUnapprovedUser={true}
                    allowHrManagerUser={true}
                  >
                    <EditProfile />
                  </PrivateRoute>
                }
              />

              <Route
                path="/account/manage"
                element={
                  <PrivateRoute allowHrManagerUser={true}>
                    <ManageAccounts />
                  </PrivateRoute>
                }
              />

              {/* Public Routes */}
              <Route
                path="/releaseNotes/:platform"
                element={<ReleaseNotes />}
              />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/about" element={<About />} />
              <Route path="/login" element={<Login />} />
              <Route path="/consent/:_id" element={<RequestConsent />} />
              <Route
                path="/threads/:contactId/:threadId"
                element={<LovedOneThread />}
              />
              <Route path="/validate" element={<Validate />} />
              <Route path="/sign-up" element={<Signup />} />
              <Route path="/sign-up-partner" element={<SignupPartner />} />
              <Route path="/signup" element={<PartnerSignup />} />
              <Route path="/forgotPassword" element={<ForgotPassword />} />
              <Route
                path="/process-booking"
                element={<ProcessBookingConfirmation />}
              />
              <Route
                path="/multi-process-booking"
                element={<MultiProcessBookingConfirmation />}
              />
              <Route
                path="/payment-success"
                element={<SuccessBookingConfirmation />}
              />
              <Route
                path="/payment-cancel"
                element={<CancelBookingConfirmation />}
              />
              <Route path="/set-active" element={<SetResidentActive />} />
              <Route path="/set-inactive" element={<SetResidentInActive />} />
              <Route
                path="/multi-status-management"
                element={<MultiStatusManager />}
              />
              <Route path="/lovedone-payments" element={<LovedOnePayment />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default AppWithRouterAccess;
